import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";

import {
  createAutoReport,
  uploadPreDistributionManualReport,
} from "../../../../actions/batchesActions";

const { REACT_APP_HOST_URL } = process.env;

const styles = {
  container: {
    mt: 2,
    p: 2,
    bgcolor: "background.paper",
    borderRadius: 1,
    border: "1px solid",
    borderColor: "divider",
  },
  reportCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  reportCardContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  reportButtons: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    mt: "auto",
  },
  reportHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 1,
  },
  uploadDialog: {
    minWidth: "400px",
  },
};

const PreDistributionReportsSection = ({
  preDistributionId,
  projectId,
  batchId,
  currentPage,
  reportsAuto,
  reportsManual,
  reportMerged,
}) => {
  const dispatch = useDispatch();

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [notes, setNotes] = useState("");

  const handleGenerateAutoReport = () => {
    dispatch(
      createAutoReport({
        preDistributionId,
        projectId,
        batchId,
        query: { page: currentPage },
      })
    );
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        alert("Solo se permiten archivos PDF");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert("El archivo excede el tamaño máximo de 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadManualReport = () => {
    if (!selectedFile) {
      alert("Por favor selecciona un archivo PDF");
      return;
    }

    dispatch(
      uploadPreDistributionManualReport({
        preDistributionId,
        projectId,
        batchId,
        query: { page: currentPage },
        file: selectedFile,
        notes: notes.trim() || null,
        callback: () => {
          setShowUploadDialog(false);
          setSelectedFile(null);
          setNotes("");
        },
      })
    );
  };

  const handleDownloadReport = (url) => {
    window.open(url, "_blank");
  };

  const handlePreviewReport = (filePath) => {
    const previewUrl = `${REACT_APP_HOST_URL}/images/reports${filePath}`;
    window.open(previewUrl, "_blank");
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const kb = bytes / 1024;
    return `${kb.toFixed(2)} KB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasReports = reportsAuto || reportsManual || reportMerged;

  // Debug: Ver qué datos llegan
  // console.log("PreDistribution Reports:", {
  //   preDistributionId,
  //   reportsAuto,
  //   reportsManual,
  //   reportMerged,
  // });

  return (
    <Box sx={styles.container}>
      <Box sx={styles.reportHeader}>
        <Typography variant="h6" fontWeight="bold">
          Reportes
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            sx={{ my: 0 }}
            startIcon={<AutoFixHighIcon />}
            onClick={handleGenerateAutoReport}
          >
            Generar Reporteº Automático
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setShowUploadDialog(true)}
          >
            Subir Manual
          </Button>
        </Stack>
      </Box>

      {!hasReports && (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          No hay reportes disponibles para esta pre-distribución
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {reportsAuto && (
          <Grid item xs={12} md={4}>
            <Card sx={styles.reportCard}>
              <CardContent sx={styles.reportCardContent}>
                <Box sx={styles.reportHeader}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Reporte Automático
                  </Typography>
                  <Chip label="Auto" color="info" size="small" />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  Generado: {formatDate(reportsAuto.generatedAt)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Tamaño: {formatFileSize(reportsAuto.size)}
                </Typography>
                <Box sx={styles.reportButtons}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handlePreviewReport(reportsAuto.filePath)}
                    disabled={!reportsAuto.filePath}
                    sx={{ flex: 1, my: 0 }}
                  >
                    Preview
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadReport(reportsAuto.fileUrl)}
                    disabled={!reportsAuto.filePath}
                    sx={{ flex: 1, my: 0 }}
                  >
                    Descargar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {reportsManual && (
          <Grid item xs={12} md={4}>
            <Card sx={styles.reportCard}>
              <CardContent sx={styles.reportCardContent}>
                <Box sx={styles.reportHeader}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Reporte Manual
                  </Typography>
                  <Chip label="Manual" color="warning" size="small" />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  Subido: {formatDate(reportsManual.uploadedAt)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Archivo: {reportsManual.originalName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Tamaño: {formatFileSize(reportsManual.size)}
                </Typography>
                {reportsManual.notes && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ mt: 1, fontStyle: "italic" }}
                  >
                    Notas: {reportsManual.notes}
                  </Typography>
                )}
                <Box sx={styles.reportButtons}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handlePreviewReport(reportsManual.filePath)}
                    disabled={!reportsManual.filePath}
                    sx={{ flex: 1, my: 0 }}
                  >
                    Preview
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadReport(reportsManual.fileUrl)}
                    disabled={!reportsManual.filePath}
                    sx={{ flex: 1, my: 0 }}
                  >
                    Descargar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {reportMerged && (
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                ...styles.reportCard,
                border: 2,
                borderColor: "success.main",
              }}
            >
              <CardContent sx={styles.reportCardContent}>
                <Box sx={styles.reportHeader}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Reporte Combinado
                  </Typography>
                  <Chip label="Merged" color="success" size="small" />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  Generado: {formatDate(reportMerged.generatedAt)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Tamaño: {formatFileSize(reportMerged.size)}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="success.main"
                  sx={{ mt: 1, fontWeight: "bold" }}
                >
                  Este es el reporte final (Auto + Manual)
                </Typography>
                <Box sx={styles.reportButtons}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handlePreviewReport(reportMerged.filePath)}
                    disabled={!reportMerged.filePath}
                    sx={{ flex: 1, my: 0 }}
                  >
                    Preview
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadReport(reportMerged.fileUrl)}
                    disabled={!reportMerged.filePath}
                    sx={{ flex: 1, my: 0 }}
                  >
                    Descargar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Subir Reporte Manual</Typography>
            <IconButton onClick={() => setShowUploadDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              id="pdf-upload-input"
            />
            <label htmlFor="pdf-upload-input">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<UploadFileIcon />}
                sx={{ mb: 2 }}
              >
                {selectedFile
                  ? `Seleccionado: ${selectedFile.name}`
                  : "Seleccionar archivo PDF"}
              </Button>
            </label>

            {selectedFile && (
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Archivo: {selectedFile.name}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Tamaño: {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
            )}

            <TextField
              label="Notas (opcional)"
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Información adicional sobre el reporte..."
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setShowUploadDialog(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadManualReport}
            disabled={!selectedFile}
          >
            Subir Reporte
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PreDistributionReportsSection;
